import React from 'react';
import { ArrowLeft, Store, Users, DollarSign, TrendingUp, CheckCircle, Clock, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BusinessInfoPage = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: <Users size={24} className="text-blue-600" />,
      title: "Access Large Customer Base",
      description: "Reach thousands of community members actively looking for bulk purchasing opportunities."
    },
    {
      icon: <DollarSign size={24} className="text-green-600" />,
      title: "Predictable Revenue",
      description: "Sell in bulk with guaranteed orders and upfront payments, improving your cash flow."
    },
    {
      icon: <TrendingUp size={24} className="text-purple-600" />,
      title: "Higher Volume Sales",
      description: "Increase your average order size through group purchases and bulk selling."
    },
    {
      icon: <CheckCircle size={24} className="text-blue-600" />,
      title: "Community Support",
      description: "Build strong relationships with local customers who value supporting local businesses."
    }
  ];

  const features = [
    {
      icon: <Clock size={20} className="text-green-600" />,
      title: "Quick Setup",
      description: "Get your vendor profile live in under 24 hours"
    },
    {
      icon: <Shield size={20} className="text-blue-600" />,
      title: "Secure Payments",
      description: "Guaranteed payments with buyer protection"
    },
    {
      icon: <Users size={20} className="text-purple-600" />,
      title: "Group Coordination",
      description: "We handle all group management and logistics coordination"
    }
  ];

  const testimonials = [
    {
      name: "Maria's Fresh Produce",
      location: "Detroit, MI",
      quote: "UniLedge helped us increase our weekly sales by 40% through bulk orders. The community really supports local businesses!",
      avatar: "MF"
    },
    {
      name: "Green Valley Farm Co-op",
      location: "Sacramento, CA",
      quote: "We love the predictable orders and upfront payments. It's transformed how we plan our harvest cycles.",
      avatar: "GV"
    }
  ];

  const handleBackToWelcome = () => {
    navigate('/welcome');
  };

  const handleGetStarted = () => {
    navigate('/register');
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-blue-50 border-b">
        <div className="px-4 py-4 flex items-center">
          <button 
            onClick={handleBackToWelcome}
            className="mr-3 p-2 hover:bg-blue-100 rounded-full transition-colors"
          >
            <ArrowLeft size={20} className="text-blue-700" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-blue-900">For Local Businesses</h1>
            <p className="text-sm text-blue-700">Join the UniLedge vendor network</p>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="px-6 py-8 text-center bg-gradient-to-b from-blue-50 to-white">
        <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 mx-auto">
          <Store size={32} />
        </div>
        <h2 className="text-2xl font-bold mb-2">Grow Your Business with Community Groups</h2>
        <p className="text-gray-600 mb-6">
          Connect with local customers, increase sales through bulk orders, and build lasting community relationships.
        </p>
        
        {/* Key Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">5K+</div>
            <div className="text-xs text-gray-600">Active Buyers</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">40%</div>
            <div className="text-xs text-gray-600">Avg. Sales Increase</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">200+</div>
            <div className="text-xs text-gray-600">Partner Vendors</div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="px-6 py-8">
        <h3 className="text-xl font-bold mb-6 text-center">How It Works</h3>
        <div className="space-y-6">
          <div className="flex items-start">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold mr-4 flex-shrink-0">
              1
            </div>
            <div>
              <h4 className="font-semibold mb-1">Create Your Vendor Profile</h4>
              <p className="text-gray-600 text-sm">Set up your business profile with products, pricing tiers, and availability.</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold mr-4 flex-shrink-0">
              2
            </div>
            <div>
              <h4 className="font-semibold mb-1">Receive Group Orders</h4>
              <p className="text-gray-600 text-sm">Community groups browse your products and place bulk orders with guaranteed payment.</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold mr-4 flex-shrink-0">
              3
            </div>
            <div>
              <h4 className="font-semibold mb-1">Fulfill & Deliver</h4>
              <p className="text-gray-600 text-sm">Prepare bulk orders and coordinate delivery or pickup with group organizers.</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold mr-4 flex-shrink-0">
              4
            </div>
            <div>
              <h4 className="font-semibold mb-1">Get Paid & Grow</h4>
              <p className="text-gray-600 text-sm">Receive secure payments and build relationships for repeat business.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Benefits */}
      <div className="px-6 py-8 bg-gray-50">
        <h3 className="text-xl font-bold mb-6 text-center">Why Partner with UniLedge?</h3>
        <div className="space-y-6">
          {benefits.map((benefit, index) => (
            <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-start">
                <div className="mr-4 flex-shrink-0">
                  {benefit.icon}
                </div>
                <div>
                  <h4 className="font-semibold mb-2">{benefit.title}</h4>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <div className="px-6 py-8">
        <h3 className="text-xl font-bold mb-6 text-center">Platform Features</h3>
        <div className="grid grid-cols-1 gap-4">
          {features.map((feature, index) => (
            <div key={index} className="flex items-center p-4 bg-blue-50 rounded-lg">
              <div className="mr-3 flex-shrink-0">
                {feature.icon}
              </div>
              <div>
                <h4 className="font-semibold text-sm">{feature.title}</h4>
                <p className="text-gray-600 text-xs">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing Tiers Example */}
      <div className="px-6 py-8 bg-green-50">
        <h3 className="text-xl font-bold mb-6 text-center">Flexible Pricing Example</h3>
        <div className="bg-white rounded-lg p-4 border border-green-200">
          <h4 className="font-semibold mb-3">Organic Tomatoes - 1 lb packages</h4>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>1-9 packages:</span>
              <span className="font-medium">$4.99 each</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>10-19 packages:</span>
              <span className="font-medium text-green-600">$4.49 each (10% off)</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>20-49 packages:</span>
              <span className="font-medium text-green-600">$3.99 each (20% off)</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>50+ packages:</span>
              <span className="font-medium text-green-600">$3.49 each (30% off)</span>
            </div>
          </div>
          <div className="mt-3 p-2 bg-green-50 rounded text-xs text-green-700">
            💡 You set the tiers, we bring the customers!
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="px-6 py-8">
        <h3 className="text-xl font-bold mb-6 text-center">Success Stories</h3>
        <div className="space-y-4">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-medium mr-3 flex-shrink-0">
                  {testimonial.avatar}
                </div>
                <div>
                  <p className="text-gray-700 mb-2 text-sm">"{testimonial.quote}"</p>
                  <div className="text-xs">
                    <span className="font-medium">{testimonial.name}</span>
                    <span className="text-gray-500"> · {testimonial.location}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Requirements */}
      <div className="px-6 py-8 bg-gray-50">
        <h3 className="text-xl font-bold mb-6 text-center">Getting Started Requirements</h3>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="space-y-3">
            <div className="flex items-start">
              <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-sm">Valid business license or registration</span>
            </div>
            <div className="flex items-start">
              <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-sm">Ability to fulfill bulk orders (minimum 10 units)</span>
            </div>
            <div className="flex items-start">
              <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-sm">Local delivery or pickup capability</span>
            </div>
            <div className="flex items-start">
              <CheckCircle size={16} className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-sm">Commitment to fair pricing and quality products</span>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="px-6 py-8 text-center">
        <h3 className="text-xl font-bold mb-4">Ready to Join Our Vendor Network?</h3>
        <p className="text-gray-600 mb-6 text-sm">
          Start connecting with local customers and growing your business today.
        </p>
        
        <button 
          onClick={handleGetStarted}
          className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium mb-3 hover:bg-blue-700 transition-colors"
        >
          Apply to Become a Vendor
        </button>
        
        <button 
          onClick={handleSignIn}
          className="w-full bg-white border border-gray-300 text-blue-600 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors mb-4"
        >
          Sign In to Existing Account
        </button>
        
        <div className="text-xs text-gray-500">
          Questions? Email us at vendors@uniledge.com
        </div>
      </div>
    </div>
  );
};

export default BusinessInfoPage;